import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Megaphone,
  Plus,
  X,
  Pin,
  Archive,
  Edit2,
  Trash2,
  AlertCircle,
  Info,
  AlertTriangle,
  Zap,
} from "lucide-react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";

const priorityConfig = {
  low: {
    icon: Info,
    color: "blue",
    bg: "from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/30",
    border: "border-blue-200 dark:border-blue-800",
    text: "text-blue-600 dark:text-blue-400",
  },
  normal: {
    icon: Megaphone,
    color: "gray",
    bg: "from-gray-50 to-slate-50 dark:from-gray-900/50 dark:to-slate-900/40",
    border: "border-gray-200 dark:border-gray-700",
    text: "text-gray-600 dark:text-gray-400",
  },
  high: {
    icon: AlertCircle,
    color: "amber",
    bg: "from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/30",
    border: "border-amber-200 dark:border-amber-800",
    text: "text-amber-600 dark:text-amber-400",
  },
  urgent: {
    icon: AlertTriangle,
    color: "red",
    bg: "from-red-50 to-rose-50 dark:from-red-950/40 dark:to-rose-950/30",
    border: "border-red-200 dark:border-red-800",
    text: "text-red-600 dark:text-red-400",
  },
};

const AnnouncementBoard = ({ team }) => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    priority: "normal",
    pinned: false,
  });

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("team_announcements")
        .select("*")
        .eq("team_id", team.id)
        .eq("archived", showArchived)
        .order("pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAnnouncements(data || []);
    } catch (error) {
      console.error("Error loading announcements:", error);
    } finally {
      setLoading(false);
    }
  }, [team.id, showArchived]);

  useEffect(() => {
    if (team?.id) {
      loadAnnouncements();
    }
  }, [team?.id, loadAnnouncements]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setError(null);

    try {
      if (editingAnnouncement) {
        // Update existing
        const { error } = await supabase
          .from("team_announcements")
          .update({
            title: formData.title,
            message: formData.message,
            priority: formData.priority,
            pinned: formData.pinned,
          })
          .eq("id", editingAnnouncement.id);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase.from("team_announcements").insert({
          team_id: team.id,
          captain_id: user.id,
          title: formData.title,
          message: formData.message,
          priority: formData.priority,
          pinned: formData.pinned,
        });

        if (error) throw error;
      }

      // Reset form and reload
      setFormData({
        title: "",
        message: "",
        priority: "normal",
        pinned: false,
      });
      setShowCreateModal(false);
      setEditingAnnouncement(null);
      loadAnnouncements();
    } catch (error) {
      console.error("Error saving announcement:", error);
      alert("Failed to save announcement");
    }
  };

  const togglePin = async (id, currentPinned) => {
    const { error } = await supabase
      .from("team_announcements")
      .update({ pinned: !currentPinned })
      .eq("id", id);

    if (!error) {
      loadAnnouncements();
    }
  };

  const toggleArchive = async (id, currentArchived) => {
    const { error } = await supabase
      .from("team_announcements")
      .update({ archived: !currentArchived })
      .eq("id", id);

    if (!error) {
      loadAnnouncements();
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    const { error } = await supabase
      .from("team_announcements")
      .delete()
      .eq("id", deleteConfirm);

    if (!error) {
      loadAnnouncements();
    }
    setDeleteConfirm(null);
  };

  const startEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      priority: announcement.priority,
      pinned: announcement.pinned,
    });
    setShowCreateModal(true);
  };

  const cancelEdit = () => {
    setEditingAnnouncement(null);
    setFormData({ title: "", message: "", priority: "normal", pinned: false });
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 border border-gray-100 dark:border-gray-700">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 sm:p-8 border border-gray-100 dark:border-gray-700 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-purple-500/10 dark:bg-purple-500/20">
            <Megaphone
              size={22}
              className="text-purple-600 dark:text-purple-400"
            />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Team Announcements
          </h2>
        </div>

        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl font-semibold text-sm transition-all ${
              showArchived
                ? "bg-gray-600 text-white"
                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
            }`}
          >
            <Archive size={18} />
            {showArchived ? "Show Active" : "Show Archived"}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white 
                       px-5 py-2.5 rounded-2xl font-semibold transition-colors shadow-sm"
          >
            <Plus size={18} /> New Announcement
          </motion.button>
        </div>
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        <AnimatePresence>
          {announcements.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {showArchived
                ? "No archived announcements"
                : "No announcements yet. Create one to get started!"}
            </div>
          ) : (
            announcements.map((announcement, index) => {
              const config =
                priorityConfig[announcement.priority] || priorityConfig.normal;
              const Icon = config.icon;

              return (
                <motion.div
                  key={announcement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-gradient-to-br ${config.bg} rounded-2xl p-6 border-2 ${config.border} relative`}
                >
                  {/* Pin Badge */}
                  {announcement.pinned && (
                    <div className="absolute top-4 right-4">
                      <Pin
                        className="text-purple-600 dark:text-purple-400"
                        size={20}
                        fill="currentColor"
                      />
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${config.bg} border ${config.border}`}
                    >
                      <Icon size={20} className={config.text} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {announcement.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(announcement.created_at).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          },
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Message */}
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-4">
                    {announcement.message}
                  </p>

                  {/* Priority Badge */}
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${config.text} bg-white dark:bg-gray-900 border ${config.border}`}
                    >
                      <Zap size={12} />
                      {announcement.priority.toUpperCase()}
                    </span>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() =>
                          togglePin(announcement.id, announcement.pinned)
                        }
                        className={`p-2 rounded-xl transition-colors ${
                          announcement.pinned
                            ? "bg-purple-600 text-white"
                            : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        }`}
                        title={announcement.pinned ? "Unpin" : "Pin"}
                      >
                        <Pin size={16} />
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => startEdit(announcement)}
                        className="p-2 rounded-xl bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 
                                   hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() =>
                          toggleArchive(announcement.id, announcement.archived)
                        }
                        className="p-2 rounded-xl bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 
                                   hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title={announcement.archived ? "Unarchive" : "Archive"}
                      >
                        <Archive size={16} />
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setDeleteConfirm(announcement.id)}
                        className="p-2 rounded-xl bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 
                                   hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-2xl w-full shadow-2xl border border-gray-200 dark:border-gray-700"
            >
              <form onSubmit={handleSubmit}>
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b dark:border-gray-800">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {editingAnnouncement
                      ? "Edit Announcement"
                      : "New Announcement"}
                  </h3>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <X size={24} className="text-gray-500" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-4">
                  {error && (
                    <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-2xl p-4 text-red-700 dark:text-red-400 text-sm">
                      {error}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-2xl 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Announcement title..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Message
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-2xl 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                      placeholder="Write your announcement here..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) =>
                          setFormData({ ...formData, priority: e.target.value })
                        }
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-2xl 
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                   focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div className="flex items-end">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.pinned}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              pinned: e.target.checked,
                            })
                          }
                          className="w-5 h-5 accent-purple-600 rounded"
                        />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Pin to top
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex gap-3 p-6 border-t dark:border-gray-800">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="flex-1 py-3 rounded-2xl border-2 border-gray-200 dark:border-gray-700 
                               text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white 
                               font-semibold transition-colors shadow-sm"
                  >
                    {editingAnnouncement ? "Update" : "Create"} Announcement
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-700 p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                Delete Announcement?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this announcement? This action
                cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 rounded-2xl border-2 border-gray-200 dark:border-gray-700
                             text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white
                             font-semibold transition-colors shadow-sm"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnnouncementBoard;
